import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/lib/api/error";

import { EXPORT_JOB_POLL_INTERVAL_MS } from "./config";
import { isExportJobDone } from "./machine";
import type { ExportJobAdapter } from "./types";

/**
 * One status poll, shared by every kind of export. It pauses in a hidden tab
 * (TanStack's default), refetches on focus, and never raises the global
 * "Something went wrong". Errors don't stop it: the tracker counts them.
 */
export function useExportJobQuery<TStatus>(
  adapter: ExportJobAdapter<TStatus>,
  jobId: string | null,
  { intervalMs = EXPORT_JOB_POLL_INTERVAL_MS } = {},
) {
  return useQuery({
    queryKey: adapter.queryKey(jobId ?? ""),
    queryFn: () => adapter.fetchStatus(jobId as string),
    enabled: !!jobId,
    staleTime: 0,
    retry: false,
    meta: { suppressGlobalErrorToast: true },
    refetchInterval: (query) => {
      const { data, status } = query.state;
      // Stop only on the latest fetch: an error after a done result (a failed
      // refocus poll) keeps polling so the next good one settles the job.
      const done =
        status !== "error" && data !== undefined && isExportJobDone(adapter.read(data));
      return done ? false : intervalMs;
    },
  });
}

/**
 * A 4xx a retry won't change: another org's job or none (404), no access
 * (403), signed out (401).
 */
export function isClientError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 429
  );
}
